import React, { useState, useRef, useEffect } from 'react';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase';
import SearchBar from './SearchBar';
import logo from '../data/newlogo.png';

function Header({
  searchText,
  onSearchChange,
  isSignedIn,
  onOpenAccount,
  onOpenOrders,
  onOpenAddresses,
  cartCount = 0,
  onOpenCart,
  showSearch = true,
  onLogoClick,
  onLogout,
  userName,
  userPhone,
  onHelp,
  showBackButton = false,
  onBackClick
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const menuRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);
  
  const handleLogout = async () => {
    setShowLogoutConfirm(false);
    try {
      await signOut(auth);
      setMenuOpen(false);
      if (onLogout) {
        onLogout();
      }
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleMenuAction = (action) => {
    setMenuOpen(false);
    action();
  };

  return (
    <header className="header">
      <div className="topbar">
        <div className="brand">
          {showBackButton ? (
            <button
              onClick={onBackClick}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                padding: '4px 8px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#374151',
                marginRight: '8px'
              }}
            >
              ←
            </button>
          ) : null}
          <button className="logo-button" type="button" onClick={onLogoClick}>
            <img src={logo} alt="Flash" className="logo-image" />
          </button>
        </div>
        
        <div className="top-actions">
          {/* Cart icon - shown when there are items */}
          {cartCount > 0 && (
            <button
              type="button"
              onClick={onOpenCart}
              aria-label={`View cart, ${cartCount} items`}
              style={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #ff6b35 0%, #e85d2a 100%)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                padding: 0,
                position: 'relative',
                boxShadow: '0 2px 8px rgba(255, 107, 53, 0.3)',
                transition: 'transform 0.15s ease',
              }}
              onTouchStart={(e) => e.currentTarget.style.transform = 'scale(0.92)'}
              onTouchEnd={(e) => e.currentTarget.style.transform = 'scale(1)'}
              onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.92)'}
              onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
              <span style={{
                position: 'absolute',
                top: -4,
                right: -4,
                minWidth: 18,
                height: 18,
                borderRadius: 9,
                background: '#fff',
                color: '#ff6b35',
                fontSize: 10,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 4px',
                boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                border: '1.5px solid #ff6b35',
              }}>
                {cartCount > 99 ? '99+' : cartCount}
              </span>
            </button>
          )}

          {isSignedIn && (
            <div className="menu-container" ref={menuRef}>
              <button
                className="menu-trigger"
                type="button"
                onClick={() => setMenuOpen(!menuOpen)}
                aria-label="Menu"
                aria-expanded={menuOpen}
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: '50%',
                  background: '#f1f5f9',
                  border: '1.5px solid #e2e8f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  padding: 0,
                  transition: 'all 0.2s',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" strokeLinecap="round">
                  <path d="M4 6h16M4 12h16M4 18h16"/>
                </svg>
              </button>
              
              {menuOpen && (
                <div className="dropdown-menu">
                  {/* User Info */}
                  <div className="menu-user-info">
                    <span className="menu-user-name">{userName || 'Welcome!'}</span>
                    {userPhone && <span className="menu-user-phone">{userPhone}</span>}
                  </div>
                  
                  <div className="menu-divider" />
                  
                  {/* Menu Items */}
                  <button
                    className="menu-item"
                    type="button"
                    onClick={() => handleMenuAction(onOpenOrders)}
                  >
                    <span className="menu-item-icon">📦</span>
                    <span>Order History</span>
                  </button>
                  
                  <button
                    className="menu-item"
                    type="button"
                    onClick={() => handleMenuAction(onOpenCart)}
                  >
                    <span className="menu-item-icon">🛒</span>
                    <span>Cart</span>
                    {cartCount > 0 && <span className="menu-cart-badge">{cartCount}</span>}
                  </button>
                  
                  <button
                    className="menu-item"
                    type="button"
                    onClick={() => handleMenuAction(onHelp)}
                  >
                    <span className="menu-item-icon">❓</span>
                    <span>Support</span>
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      {showSearch && <SearchBar value={searchText} onChange={onSearchChange} />}
      
      {showLogoutConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '24px',
            maxWidth: '400px',
            width: '100%',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: '#fef3c7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                fontSize: '32px'
              }}>
                🚪
              </div>
              <h3 style={{ margin: '0 0 8px', fontSize: '20px', fontWeight: '600', color: 'var(--text-primary)' }}>
                Logout?
              </h3>
              <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-secondary)', lineHeight: '1.5' }}>
                Are you sure you want to logout?
              </p>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setShowLogoutConfirm(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '10px',
                  border: '1px solid var(--border-default)',
                  background: 'white',
                  color: 'var(--text-primary)',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '10px',
                  border: 'none',
                  background: 'var(--primary)',
                  color: 'white',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;
